import { useState, useEffect, useCallback } from 'react';
import { useStateValue } from '../context/StateProvider';
import { toast } from 'react-toastify';

interface DaySchedule {
    start: string;
    end: string;
}

interface ScheduleData {
    [key: string]: DaySchedule; // "monday", "tuesday", ...
}

export const useSchedule = () => {
    const [{ schedule }, dispatch] = useStateValue();
    const [isWorking, setIsWorking] = useState<boolean>(true);
    const [canOrder, setCanOrder] = useState<boolean>(true);
    const [closingTime, setClosingTime] = useState<string>('');
    const [todaySchedule, setTodaySchedule] = useState<DaySchedule | null>(null);
    const [nextWorkingDay, setNextWorkingDay] = useState<string>('');

    // Получение дня недели на русском
    const getDayName = (date: Date): string => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[date.getDay()];
    };

    // Получение текущего времени в московском часовом поясе
    const getMoscowTime = (): Date => {
        const now = new Date();
        const moscowOffset = 3; // UTC+3
        const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
        return new Date(utcTime + moscowOffset * 3600000);
    };

    // Получение времени в формате HH:MM
    const getTimeString = (date: Date): string => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // Получение расписания на сегодня
    const getTodaySchedule = useCallback((): DaySchedule | null => {
        if (!schedule) return null;

        const moscowTime = getMoscowTime();
        const dayName = getDayName(moscowTime);

        return schedule[dayName] || null;
    }, [schedule]);

    // Проверка, работает ли ресторан сейчас
    const checkIsWorking = useCallback((): boolean => {
        const today = getTodaySchedule();
        if (!today) return false;

        const moscowTime = getMoscowTime();
        const currentTime = getTimeString(moscowTime);

        return currentTime >= today.start && currentTime < today.end;
    }, [getTodaySchedule]);

    // Проверка, можно ли делать заказы (с учетом времени до закрытия)
    const checkCanOrder = useCallback((): boolean => {
        const today = getTodaySchedule();
        if (!today) return false;

        const moscowTime = getMoscowTime();
        const currentTime = getTimeString(moscowTime);

        // Заказы принимаются только в рабочее время
        if (currentTime < today.start || currentTime >= today.end) {
            return false;
        }

        // Заказы не принимаются за 30 минут до закрытия
        const [endHours, endMinutes] = today.end.split(':').map(Number);
        const endDate = new Date(moscowTime);
        endDate.setHours(endHours, endMinutes, 0, 0);

        const diffMinutes = (endDate.getTime() - moscowTime.getTime()) / 60000;

        return diffMinutes >= 30;
    }, [getTodaySchedule]);

    // Получение следующего рабочего дня
    const getNextWorkingDay = useCallback((): string => {
        if (!schedule) return '';

        const moscowTime = getMoscowTime();
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayNames = {
            monday: 'Понедельник',
            tuesday: 'Вторник',
            wednesday: 'Среда',
            thursday: 'Четверг',
            friday: 'Пятница',
            saturday: 'Суббота',
            sunday: 'Воскресенье'
        };

        let currentDayIndex = moscowTime.getDay();

        // Проверяем следующие 7 дней
        for (let i = 1; i <= 7; i++) {
            const nextDayIndex = (currentDayIndex + i) % 7;
            const dayName = days[nextDayIndex];

            if (schedule[dayName]) {
                // Проверяем, что день рабочий
                const daySchedule = schedule[dayName];
                const now = getMoscowTime();
                const currentDayDate = new Date(now);
                currentDayDate.setDate(currentDayDate.getDate() + i);

                // Если сегодня и еще не закрылись
                if (i === 0 && checkIsWorking()) {
                    continue;
                }

                return dayNames[dayName as keyof typeof dayNames] || dayName;
            }
        }

        return 'завтра';
    }, [schedule, checkIsWorking]);

    // Обновление статуса
    const updateStatus = useCallback(() => {
        const today = getTodaySchedule();
        const isWorkingNow = checkIsWorking();
        const canOrderNow = checkCanOrder();

        setTodaySchedule(today);
        setIsWorking(isWorkingNow);
        setCanOrder(canOrderNow);

        if (today) {
            setClosingTime(today.end);
        }

        if (!isWorkingNow || !canOrderNow) {
            const nextDay = getNextWorkingDay();
            setNextWorkingDay(nextDay);
        }
    }, [getTodaySchedule, checkIsWorking, checkCanOrder, getNextWorkingDay]);

    // Обновляем статус каждую минуту
    useEffect(() => {
        updateStatus();

        const interval = setInterval(() => {
            updateStatus();
        }, 60000); // Каждую минуту

        return () => clearInterval(interval);
    }, [updateStatus]);

    return {
        isWorking,
        canOrder,
        closingTime,
        todaySchedule,
        nextWorkingDay,
        updateStatus,
        getMoscowTime,
        getTimeString,
    };
};

export const useOrderAvailability = () => {
    const { canOrder, isWorking, closingTime, todaySchedule } = useSchedule();

    const checkBeforeOrder = (): boolean => {
        if (!todaySchedule) {
            toast.error('Не удалось проверить график работы');
            return false;
        }

        if (!isWorking) {
            toast.error('Ресторан сейчас закрыт');
            return false;
        }

        if (!canOrder) {
            toast.error(`Заказы не принимаются. Ресторан работает до ${closingTime}`);
            return false;
        }

        return true;
    };

    return {
        canOrder,
        isWorking,
        closingTime,
        todaySchedule,
        checkBeforeOrder,
    };
};